import { ThemeContext } from '@/theme/ThemeProvider';
import { useContext, useRef, useState, useEffect } from 'react';
import { Carousel } from 'antd';
import type { CarouselRef } from 'antd/es/carousel';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import * as S from './styles';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Mission } from '@/app/hooks/trade/tradeMission';
import { useIsMobile } from '@/utils/CalVW';
import useNavigationHandler from '../../../useNavigationHandler';
import { formatDecimal } from '@/utils/formatDecimal';
import BigNumber from 'bignumber.js';

interface DurationListProps {
  mission: Mission;
  detailMission: Mission;
  getMissionReward: (
    missionId: number,
    tierIndex?: number,
    rewardAmount?: string | number
  ) => void;
}

const DurationList = ({
  mission,
  detailMission,
  getMissionReward,
}: DurationListProps) => {
  const [slidesToShow, setSlidesToShow] = useState(3);

  const carouselRef = useRef<CarouselRef>(null);
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useContext(ThemeContext);
  const isMobile = useIsMobile();
  const handleNavigation = useNavigationHandler();

  const list = detailMission?.conditions?.tiers ?? [];
  const userProgress = mission?.userProgress;
  const isKo = i18n.language === 'ko';
  const targetType = detailMission?.conditions?.targetType as 'net_volume' | 'net_deposit' | 'net_profit' | undefined;

  // targetType에 따른 라벨 텍스트
  const getTargetTypeLabel = () => {
    switch (targetType) {
      case 'net_volume':
        return isKo ? '기간 내 거래량' : 'Trading Volume in Period';
      case 'net_deposit':
        return isKo ? '기간 내 입금량' : 'Deposit Amount in Period';
      case 'net_profit':
        return isKo ? '기간 내 순수익' : 'Net Profit in Period';
      default:
        return isKo ? '기간 내 거래량' : 'Trading Volume in Period';
    }
  };

  // 타임존 정보 생성
  const formatTimezoneInfo = () => {
    if (!detailMission?.endDate) return '';

    const endDate = new Date(detailMission.endDate);

    // UTC 시간 포맷
    const formatTime = (date: Date) => {
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth(); // 0-11
      const day = date.getUTCDate();
      const hour = String(date.getUTCHours()).padStart(2, '0');
      const min = String(date.getUTCMinutes()).padStart(2, '0');

      if (isKo) {
        return `${year}년 ${month + 1}월 ${day}일 ${hour}:${min}`;
      } else {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${year} ${monthNames[month]} ${day} ${hour}:${min}`;
      }
    };

    const utc = formatTime(endDate);
    const kst = formatTime(new Date(endDate.getTime() + 9 * 60 * 60 * 1000));  // +9시간
    const cst = formatTime(new Date(endDate.getTime() + 8 * 60 * 60 * 1000));  // +8시간
    const est = formatTime(new Date(endDate.getTime() - 5 * 60 * 60 * 1000));  // -5시간
    const pst = formatTime(new Date(endDate.getTime() - 8 * 60 * 60 * 1000));  // -8시간

    if (isKo) {
      return `미션 종료: UTC ${utc} (한국시간 ${kst})\n주요 지역: 일본 ${kst} | 중국 ${cst} | 미국 동부 ${est} | 미국 서부 ${pst}`;
    } else {
      return `Mission ends: UTC ${utc} (Korea Time: ${kst})\nOther regions: Japan ${kst} | China ${cst} | US Eastern ${est} | US Pacific ${pst}`;
    }
  };
  /**
   * Tier 상태 계산 함수
   * @param tierIndex - Tier 인덱스 (0부터 시작)
   * @returns 'claimed' | 'active' | 'inactive'
   */
  const getTierStatus = (
    tierIndex: number
  ): 'claimed' | 'active' | 'inactive' => {
    if (!userProgress) return 'inactive';

    // 이미 보상 받은 단계
    if (userProgress.claimedTiers?.includes(tierIndex)) {
      return 'claimed';
    }
    // 달성했지만 아직 받지 않은 단계 (수령 가능)
    if (userProgress.availableTiers?.includes(tierIndex)) {
      return 'active';
    }
    // 아직 달성하지 못한 단계
    return 'inactive';
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1000) {
        setSlidesToShow(2);
      } else {
        setSlidesToShow(3);
      }
    };

    handleResize(); // 초기 설정
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const next = () => {
    carouselRef.current?.next();
  };

  const prev = () => {
    carouselRef.current?.prev();
  };

  return (
    <Container isDarkmode={isDarkMode} isMobile={isMobile}>
      <CarouselWrapper>
        <NavigationButton
          position="left"
          onClick={prev}
          isDarkmode={isDarkMode}
        >
          <LeftOutlined />
        </NavigationButton>

        <StyledCarousel
          ref={carouselRef}
          slidesToShow={slidesToShow}
          slidesToScroll={1}
          infinite={false}
          dots={false}
          draggable={false}
          swipeToSlide={true}
        >
          {(list ?? []).map((tier, index) => {
            const tierStatus = getTierStatus(index);

            const statusText =
              tierStatus === 'active'
                ? 'Claim Reward'
                : tierStatus === 'inactive'
                  ? 'not completed'
                  : tierStatus === 'claimed'
                    ? 'Rewarded'
                    : '';

            return (
              <div key={index}>
                <DurationCard isDarkmode={isDarkMode} status={tierStatus}>
                  <DurationTitle isDarkmode={isDarkMode} status={tierStatus}>
                    {isKo ? `${index + 1}단계` : `${index + 1} STEP`}{' '}
                    {(tierStatus === 'active' || tierStatus === 'claimed') &&
                      (isKo ? '달성!' : 'Achieved!')}
                  </DurationTitle>
                  <DurationContent isDarkmode={isDarkMode} status={tierStatus}>
                    <DurationDescription>
                      {t('durationMissionDescription', {
                        amount: new BigNumber(tier?.minAmount || 0).toFormat(),
                      })}
                    </DurationDescription>
                    <DurationReward isDarkmode={isDarkMode} status={tierStatus}>
                      {tier.rewardAmount} USDT
                    </DurationReward>
                    <DurationBadge
                      as="button"
                      isDarkmode={isDarkMode}
                      status={tierStatus}
                      onClick={() => {
                        if (tierStatus === 'active') {
                          getMissionReward(
                            detailMission.id,
                            index,
                            tier?.rewardAmount
                          );
                        }
                      }}
                      disabled={tierStatus !== 'active'}
                    >
                      {t(statusText)}
                    </DurationBadge>
                  </DurationContent>
                </DurationCard>
              </div>
            );
          })}
        </StyledCarousel>

        <NavigationButton
          position="right"
          onClick={next}
          isDarkmode={isDarkMode}
        >
          <RightOutlined />
        </NavigationButton>
      </CarouselWrapper>

      <DurationFooter>
        <DurationFooterLeft isDarkmode={isDarkMode}>
          <span>
            {getTargetTypeLabel()}:{' '}
            {formatDecimal(userProgress?.progress?.current ?? 0, 2)} USDT
          </span>
          <span>
            {isKo ? '전체 달성률' : 'Overall Progress'}:{' '}
            {userProgress?.progress?.percentage ?? 0}%
          </span>
        </DurationFooterLeft>
        <DurationFooterRightButton onClick={() => handleNavigation('/trade/2')}>
          {t('tradeNow')}
        </DurationFooterRightButton>
      </DurationFooter>

      <TimezoneInfoContainer isDarkmode={isDarkMode}>
        <TimezoneInfoIcon>🕐</TimezoneInfoIcon>
        <TimezoneInfoContent>
          {formatTimezoneInfo()}
        </TimezoneInfoContent>
      </TimezoneInfoContainer>
    </Container>
  );
};

export default DurationList;

export const Container = styled.div<{ isDarkmode: boolean; isMobile: boolean }>`
  padding: 36px;
  background-color: ${({ isDarkmode }) => (isDarkmode ? '#191A19' : '#E8EBEA')};
  border-radius: 8px;

  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? '24px' : '32px')};
`;

const CarouselWrapper = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const StyledCarousel = styled(Carousel)`
  .slick-slide {
    padding: 0 8px;
  }

  .slick-list {
    margin: 0 -8px;
  }
`;

const NavigationButton = styled.button<{
  position: 'left' | 'right';
  isDarkmode: boolean;
}>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ position }) => (position === 'left' ? 'left: -40px;' : 'right: -40px;')}
  width: 32px;
  height: 32px;
  border: 1px solid ${({ isDarkmode }) => (isDarkmode ? '#444' : '#ddd')};
  background: ${({ isDarkmode }) => (isDarkmode ? '#1a1a1a' : '#fff')};
  color: ${({ isDarkmode }) => (isDarkmode ? '#fff' : '#333')};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ isDarkmode }) => (isDarkmode ? '#333' : '#f5f5f5')};
    border-color: #ff5c17;
    color: #ff5c17;
  }
`;

const DurationCard = styled.div<{ isDarkmode: boolean; status: string }>`
  background: ${({ isDarkmode, status }) => {
    if (isDarkmode) {
      return status === 'active' || status === 'claimed'
        ? 'rgba(31, 55, 45, 0.4)'
        : 'rgba(31, 55, 45, 0.2)';
    } else {
      return status === 'active' || status === 'claimed'
        ? '#DEE2E1'
        : '#E6E6E6';
    }
  }};
  border-radius: 8px;
  border: ${({ isDarkmode, status }) => {
    if (isDarkmode) {
      return status === 'active' || status === 'claimed'
        ? '1px solid #00FF9C'
        : '1px solid #1F372D';
    } else {
      return status === 'active' || status === 'claimed'
        ? '1px solid #00B56F'
        : '1px solid rgba(0, 181, 111, 0.6)';
    }
  }};
`;

const DurationTitle = styled.div<{ isDarkmode: boolean; status: string }>`
  border-bottom: ${({ isDarkmode, status }) => {
    if (isDarkmode) {
      return status === 'active' || status === 'claimed'
        ? '1px solid #00FF9C'
        : '1px solid #1F372D';
    } else {
      return status === 'active' || status === 'claimed'
        ? '1px solid #00B56F'
        : '1px solid rgba(0, 181, 111, 0.6)';
    }
  }};

  color: ${({ isDarkmode, status }) => {
    if (isDarkmode) {
      return status === 'active' || status === 'claimed'
        ? '#00FF9C'
        : '#83838C';
    } else {
      return status === 'active' || status === 'claimed'
        ? '#00B56F'
        : '#757575';
    }
  }};

  font-size: 16px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DurationContent = styled.div<{ isDarkmode: boolean; status: string }>`
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const DurationDescription = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.COLORS.sectionDesc};
  line-height: 1.8;
  text-align: left;
  white-space: pre-line;
  opacity: 0.8;
  flex: 1;
`;

const DurationReward = styled.div<{ isDarkmode: boolean; status: string }>`
  color: ${({ isDarkmode, status, theme }) => {
    if (isDarkmode) {
      return status === 'active' || status === 'claimed'
        ? theme.COLORS.textw
        : '#474747';
    } else {
      return status === 'active' || status === 'claimed'
        ? theme.COLORS.textw
        : '#9E9E9E';
    }
  }};
  font-weight: 600;
  font-size: 32px;
`;

const DurationBadge = styled.div<{ isDarkmode: boolean; status: string }>`
  padding: 10px 20px;
  border-radius: 6px;
  border: none;
  cursor: ${({ status }) => (status === 'active' ? 'pointer' : 'not-allowed')};
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: ${({ status }) => (status === 'active' ? 600 : 400)};
  font-family: 'Pretendard', sans-serif;
  line-height: 1.25;

  background-color: ${({ status, isDarkmode }) => {
    if (status === 'active') {
      return isDarkmode ? '#00FF9C' : '#00B56F'; // 초록색 (수령 가능)
    }
    if (status === 'claimed') {
      return isDarkmode ? 'rgba(0, 255, 156, 0.15)' : 'rgba(0, 181, 111, 0.15)'; // 연한 초록 (수령 완료)
    }
    return isDarkmode ? '#2A2A2A' : '#E0E0E0'; // 회색 (비활성)
  }};

  color: ${({ status, isDarkmode }) => {
    if (status === 'active') {
      return isDarkmode ? '#191A19' : '#FFFFFF'; // 다크모드: 어두운 텍스트, 라이트모드: 흰색
    }
    if (status === 'claimed') {
      return isDarkmode ? '#00FF9C' : '#00B56F'; // 초록색 텍스트 (수령 완료)
    }
    return isDarkmode ? '#83838C' : '#757575'; // 회색 (비활성)
  }};

  &:hover {
    ${({ status, isDarkmode }) =>
      status === 'active' &&
      `
      background-color: ${isDarkmode ? '#00E68C' : '#009E5F'};
      box-shadow: 0 2px 6px ${isDarkmode ? 'rgba(0, 255, 156, 0.25)' : 'rgba(0, 181, 111, 0.3)'};
    `}
  }

  &:active {
    ${({ status, isDarkmode }) =>
      status === 'active' &&
      `
      background-color: ${isDarkmode ? '#00CC7C' : '#008E55'};
      box-shadow: 0 1px 3px ${isDarkmode ? 'rgba(0, 255, 156, 0.2)' : 'rgba(0, 181, 111, 0.25)'};
    `}
  }

  &:disabled {
    opacity: ${({ status }) => (status === 'claimed' ? 0.7 : 1)};
  }
`;

const DurationFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DurationFooterLeft = styled.div<{ isDarkmode: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: ${({ isDarkmode }) => (isDarkmode ? '#83838C' : '#757575')};
  font-size: 16px;
  font-weight: 400;
  line-height: 1.25;
`;

const DurationFooterRightButton = styled.button`
  width: 216px;
  height: 54px;
  border-radius: 4px;
  color: #ff5c17;
  border: 1px solid #ff5c17;
  font-family: 'Pretendard', sans-serif;
  background: transparent;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #ff5c17;
    color: white;
  }
`;

const TimezoneInfoContainer = styled.div<{ isDarkmode: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 24px;
  padding: 20px 24px;
  background-color: ${({ isDarkmode }) =>
    isDarkmode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 8px;
`;

const TimezoneInfoIcon = styled.span`
  font-size: 20px;
  line-height: 1.4;
  opacity: 0.6;
  flex-shrink: 0;
`;

const TimezoneInfoContent = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: ${({ theme }) => theme.COLORS.sectionDesc};
  line-height: 1.8;
  text-align: left;
  white-space: pre-line;
  opacity: 0.8;
  flex: 1;
`;
